import React, { useRef, useCallback, useState, useEffect } from "react";
import PropTypes from "prop-types";
import classNames from "classnames";
import {
  OVERLAY_SIZES,
  MODAL_WIDTH_MAP,
  OVERLAY_BASE_Z_INDEX,
  OVERLAY_Z_INDEX_STEP,
  MODAL_CLOSE_DURATION,
} from "@/components/overlays/constants/overlayDefaults";

/**
 * OverlayModal — pure React/Tailwind centred modal for the overlay system.
 *
 * Uses only React state and CSS transitions so that all React synthetic event
 * handlers inside the rendered content work correctly.  Syncfusion's
 * DialogComponent was removed because its internal vanilla-JS DOM manipulation
 * breaks React's event delegation on children.
 *
 * Two-phase animation:
 *   mount  → fade-in + scale-up
 *   close  → fade-out + scale-down, then unmount
 */
export default function OverlayModal({ entry, stackIndex, isTopmost, onClose, onRemove }) {
  const removeTimerRef = useRef(null);
  const { key, Component, props, options, closing } = entry;

  const size = options?.size || OVERLAY_SIZES.MD;
  const width = MODAL_WIDTH_MAP[size] || MODAL_WIDTH_MAP[OVERLAY_SIZES.MD];
  const backdropZ = OVERLAY_BASE_Z_INDEX + stackIndex * OVERLAY_Z_INDEX_STEP;
  const panelZ = backdropZ + 1;

  // Controls the CSS transition. Starts false, becomes true after mount.
  const [visible, setVisible] = useState(false);

  // Phase 1 — fade/scale in after initial render
  useEffect(() => {
    const frame = requestAnimationFrame(() => setVisible(true));
    return () => cancelAnimationFrame(frame);
  }, []);

  // Phase 2 — when closing flag flips, animate out then remove
  useEffect(() => {
    if (closing) {
      queueMicrotask(() => setVisible(false));
      removeTimerRef.current = setTimeout(() => {
        onRemove(key);
      }, MODAL_CLOSE_DURATION + 50);
    }
    return () => clearTimeout(removeTimerRef.current);
  }, [closing, key, onRemove]);

  const handleClose = useCallback(() => {
    onClose(key);
  }, [onClose, key]);

  const handleBackdropClick = useCallback(() => {
    if (isTopmost) {
      handleClose();
    }
  }, [isTopmost, handleClose]);

  // Prevent clicks inside the dialog panel from bubbling to the backdrop
  const handlePanelClick = useCallback(e => {
    e.stopPropagation();
  }, []);

  return (
    <>
      {/* Backdrop */}
      <div
        className={classNames(
          "fixed inset-0 bg-gray-500/75 transition-opacity",
          visible ? "opacity-100" : "opacity-0",
        )}
        style={{ zIndex: backdropZ, transitionDuration: `${MODAL_CLOSE_DURATION}ms` }}
        aria-hidden="true"
      />

      {/* Centred panel container — clicking the outer area closes the modal */}
      <div
        role="dialog"
        aria-modal="true"
        className="fixed inset-0 overflow-y-auto"
        style={{ zIndex: panelZ }}
        onClick={handleBackdropClick}
      >
        <div className="flex min-h-full items-end justify-center px-4 pt-12 pb-4 text-center sm:items-center sm:p-0">
          <div
            className={classNames(
              "relative w-full inline-block overflow-hidden rounded-xl bg-white dark:bg-slate-900 text-left shadow-2xl transform transition-all sm:my-8",
              visible
                ? "opacity-100 translate-y-0 sm:scale-100"
                : "opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95",
            )}
            style={{ maxWidth: width, transitionDuration: `${MODAL_CLOSE_DURATION}ms` }}
            onClick={handlePanelClick}
          >
            <Component {...props} handleClose={handleClose} />
          </div>
        </div>
      </div>
    </>
  );
}

OverlayModal.propTypes = {
  entry: PropTypes.shape({
    key: PropTypes.string.isRequired,
    Component: PropTypes.elementType.isRequired,
    props: PropTypes.object,
    options: PropTypes.object,
    closing: PropTypes.bool,
  }).isRequired,
  stackIndex: PropTypes.number.isRequired,
  isTopmost: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onRemove: PropTypes.func.isRequired,
};
